import React, { useEffect, useRef, useCallback, useState } from 'react';
import { Terminal } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import posthog from 'posthog-js';
import 'xterm/css/xterm.css';
import { WS_BASE_URL } from '../config/constants';

const TERMINAL_WS_URL = `${WS_BASE_URL}/terminal`;

// Single terminal session component
function TerminalSession({ isActive, sessionId, onStatusChange }) {
  const terminalRef = useRef(null);
  const terminalInstance = useRef(null);
  const fitAddon = useRef(null);
  const wsRef = useRef(null);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');

  // Notify parent of status changes
  useEffect(() => {
    onStatusChange?.(sessionId, connectionStatus);
  }, [connectionStatus, sessionId, onStatusChange]);

  const connectTerminal = useCallback(() => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      return;
    }

    setConnectionStatus('connecting');

    const ws = new WebSocket(TERMINAL_WS_URL);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnectionStatus('connected');
      posthog.capture('terminal_connected');

      setTimeout(() => {
        if (terminalInstance.current) {
          const cols = terminalInstance.current.cols || 80;
          const rows = terminalInstance.current.rows || 24;
          ws.send(JSON.stringify({ type: 'resize', cols, rows }));
        }
      }, 50);
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);

        switch (msg.type) {
          case 'output':
            terminalInstance.current?.write(msg.data);
            break;
          case 'exit':
            terminalInstance.current?.write('\r\n[Process exited]\r\n');
            setConnectionStatus('disconnected');
            break;
          case 'error':
            terminalInstance.current?.write(`\r\n[Error: ${msg.message}]\r\n`);
            setConnectionStatus('error');
            break;
          default:
            break;
        }
      } catch (e) {
        console.error('Failed to parse terminal message:', e);
      }
    };

    ws.onclose = () => {
      setConnectionStatus('disconnected');
    };

    ws.onerror = (error) => {
      setConnectionStatus('error');
      console.error('Terminal WebSocket error:', error);
    };
  }, []);

  const handleResize = useCallback(() => {
    if (!fitAddon.current || !terminalInstance.current || !terminalRef.current) {
      return;
    }

    const container = terminalRef.current;
    if (!container || container.clientWidth === 0 || container.clientHeight === 0) {
      return;
    }

    if (!terminalInstance.current.element) {
      return;
    }

    try {
      fitAddon.current.fit();

      const cols = terminalInstance.current.cols;
      const rows = terminalInstance.current.rows;

      if (cols && rows && wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'resize', cols, rows }));
      }
    } catch (e) {
      console.debug('Terminal resize error:', e.message);
    }
  }, []);

  useEffect(() => {
    if (!terminalRef.current || terminalInstance.current) return;

    const terminal = new Terminal({
      cursorBlink: true,
      fontSize: 12,
      fontFamily: 'Menlo, Monaco, "Courier New", monospace',
      theme: {
        background: '#ffffff',
        foreground: '#1e1e1e',
        cursor: '#1e1e1e',
        cursorAccent: '#ffffff',
        selection: 'rgba(0, 0, 0, 0.15)',
        black: '#1e1e1e',
        red: '#cd3131',
        green: '#0dbc79',
        yellow: '#c19c00',
        blue: '#2472c8',
        magenta: '#bc3fbc',
        cyan: '#11a8cd',
        white: '#e5e5e5',
        brightBlack: '#666666',
        brightRed: '#f14c4c',
        brightGreen: '#23d18b',
        brightYellow: '#c19c00',
        brightBlue: '#3b8eea',
        brightMagenta: '#d670d6',
        brightCyan: '#29b8db',
        brightWhite: '#1e1e1e'
      },
      scrollback: 5000,
      allowProposedApi: true
    });

    terminalInstance.current = terminal;

    const fit = new FitAddon();
    fitAddon.current = fit;
    terminal.loadAddon(fit);

    const openTerminalWhenReady = () => {
      const container = terminalRef.current;
      if (!container) return;

      if (container.clientWidth > 0 && container.clientHeight > 0) {
        try {
          terminal.open(container);
          handleResize();
        } catch (e) {
          console.debug('Terminal open error:', e.message);
        }
      } else {
        requestAnimationFrame(openTerminalWhenReady);
      }
    };

    const fitTimer = setTimeout(() => {
      requestAnimationFrame(openTerminalWhenReady);
    }, 50);

    terminal.onData((data) => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: 'input', data }));
        // Track command submissions (Enter key)
        if (data === '\r') {
          posthog.capture('terminal_command_sent');
        }
      }
    });

    const connectTimer = setTimeout(() => {
      connectTerminal();
    }, 100);

    window.addEventListener('resize', handleResize);

    return () => {
      clearTimeout(fitTimer);
      clearTimeout(connectTimer);
      window.removeEventListener('resize', handleResize);
      wsRef.current?.close();
      terminal.dispose();
      terminalInstance.current = null;
      fitAddon.current = null;
    };
  }, [connectTerminal, handleResize]);

  // Handle visibility changes
  useEffect(() => {
    if (isActive && terminalRef.current) {
      const resizeObserver = new ResizeObserver(() => {
        if (terminalRef.current && terminalRef.current.clientWidth > 0 && terminalRef.current.clientHeight > 0 && terminalInstance.current?.element) {
          handleResize();
        }
      });

      resizeObserver.observe(terminalRef.current);

      const timer = setTimeout(() => {
        if (terminalRef.current && terminalRef.current.clientWidth > 0 && terminalInstance.current?.element) {
          handleResize();
          terminalInstance.current?.focus();
        }
      }, 350);

      return () => {
        clearTimeout(timer);
        resizeObserver.disconnect();
      };
    }
  }, [isActive, handleResize]);

  const handleReconnect = () => {
    wsRef.current?.close();
    terminalInstance.current?.clear();
    connectTerminal();
  };

  return (
    <div style={{
      display: isActive ? 'flex' : 'none',
      flexDirection: 'column',
      width: '100%',
      height: '100%',
      background: '#ffffff'
    }}>
      {connectionStatus !== 'connected' && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 16px',
          background: '#fafafa',
          borderBottom: '1px solid #e5e5e5'
        }}>
          <span style={{
            fontSize: '12px',
            padding: '4px 10px',
            borderRadius: '6px',
            background: connectionStatus === 'connecting' ? '#fef3c7' :
                       connectionStatus === 'error' ? '#fee2e2' : '#f3f4f6',
            color: connectionStatus === 'connecting' ? '#92400e' :
                   connectionStatus === 'error' ? '#991b1b' : '#4b5563',
            fontWeight: '500'
          }}>
            {connectionStatus}
          </span>
          <button
            onClick={handleReconnect}
            style={{
              padding: '6px 12px',
              fontSize: '13px',
              background: '#3b82f6',
              color: '#fff',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              fontWeight: '500',
              transition: 'background 0.15s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.background = '#2563eb'}
            onMouseLeave={(e) => e.currentTarget.style.background = '#3b82f6'}
          >
            Reconnect
          </button>
        </div>
      )}

      <div
        ref={terminalRef}
        style={{
          flex: 1,
          padding: '12px 16px',
          overflow: 'hidden',
          boxSizing: 'border-box'
        }}
        onClick={() => terminalInstance.current?.focus()}
      />
    </div>
  );
}

// Main terminal view with tabs
function TerminalView({ isVisible = true }) {
  const [tabs, setTabs] = useState([{ id: 1, name: 'bash' }]);
  const [activeTabId, setActiveTabId] = useState(1);
  const [tabStatuses, setTabStatuses] = useState({});
  const [editingTabId, setEditingTabId] = useState(null);
  const [editValue, setEditValue] = useState('');
  const editInputRef = useRef(null);
  const nextTabId = useRef(2);

  const handleStatusChange = useCallback((sessionId, status) => {
    setTabStatuses(prev => ({ ...prev, [sessionId]: status }));
  }, []);

  const startEditing = (tabId, currentName, e) => {
    e.stopPropagation();
    setEditingTabId(tabId);
    setEditValue(currentName);
    setTimeout(() => editInputRef.current?.select(), 0);
  };

  const saveTabName = () => {
    if (editingTabId && editValue.trim()) {
      setTabs(prev => prev.map(t =>
        t.id === editingTabId ? { ...t, name: editValue.trim() } : t
      ));
    }
    setEditingTabId(null);
    setEditValue('');
  };

  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter') {
      saveTabName();
    } else if (e.key === 'Escape') {
      setEditingTabId(null);
      setEditValue('');
    }
  };

  const addTab = () => {
    const newId = nextTabId.current++;
    setTabs(prev => [...prev, { id: newId, name: 'bash' }]);
    setActiveTabId(newId);
  };

  const closeTab = (tabId, e) => {
    e.stopPropagation();

    if (tabs.length === 1) {
      // Don't close the last tab, just keep it
      return;
    }

    const tabIndex = tabs.findIndex(t => t.id === tabId);
    const newTabs = tabs.filter(t => t.id !== tabId);
    setTabs(newTabs);

    // If closing active tab, switch to adjacent tab
    if (activeTabId === tabId) {
      const newIndex = Math.min(tabIndex, newTabs.length - 1);
      setActiveTabId(newTabs[newIndex].id);
    }

    // Clean up status
    setTabStatuses(prev => {
      const { [tabId]: _, ...rest } = prev;
      return rest;
    });
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      height: '100%',
      background: '#ffffff'
    }}>
      {/* Tab Bar */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        background: '#fafafa',
        borderBottom: '1px solid #e5e5e5',
        height: '42px',
        flexShrink: 0,
        paddingLeft: '12px'
      }}>
        {/* Tabs */}
        <div style={{
          display: 'flex',
          overflow: 'auto',
          height: '100%',
          gap: '4px'
        }}>
          {tabs.map((tab, index) => {
            const isActive = tab.id === activeTabId;
            const status = tabStatuses[tab.id];

            return (
              <div
                key={tab.id}
                onClick={() => setActiveTabId(tab.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '0 16px',
                  height: 'calc(100% - 2px)',
                  marginTop: '2px',
                  cursor: 'pointer',
                  background: isActive ? '#ffffff' : 'transparent',
                  borderTopLeftRadius: '8px',
                  borderTopRightRadius: '8px',
                  borderBottom: isActive ? '2px solid #3b82f6' : '2px solid transparent',
                  color: isActive ? '#1e1e1e' : '#6b7280',
                  fontSize: '13px',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.15s',
                  fontWeight: isActive ? '500' : '400',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = '#f3f4f6';
                    e.currentTarget.style.color = '#1e1e1e';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = '#6b7280';
                  }
                }}
              >
                {/* Terminal icon */}
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" style={{ width: '16px', height: '16px', opacity: isActive ? 0.7 : 0.5 }}>
                  <path d="M3.5 2A1.5 1.5 0 0 0 2 3.5v9A1.5 1.5 0 0 0 3.5 14h9a1.5 1.5 0 0 0 1.5-1.5v-9A1.5 1.5 0 0 0 12.5 2h-9ZM5 6.5a.5.5 0 0 1 .146-.354l2-2a.5.5 0 1 1 .708.708L6.207 6.5l1.647 1.646a.5.5 0 0 1-.708.708l-2-2A.5.5 0 0 1 5 6.5Zm3.5 3a.5.5 0 0 1 .5-.5h2a.5.5 0 0 1 0 1H9a.5.5 0 0 1-.5-.5Z"/>
                </svg>
                {editingTabId === tab.id ? (
                  <input
                    ref={editInputRef}
                    type="text"
                    value={editValue}
                    onChange={(e) => setEditValue(e.target.value)}
                    onBlur={saveTabName}
                    onKeyDown={handleEditKeyDown}
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      background: '#ffffff',
                      border: '1px solid #3b82f6',
                      borderRadius: '4px',
                      color: '#1e1e1e',
                      fontSize: '13px',
                      padding: '2px 6px',
                      width: '90px',
                      outline: 'none',
                      fontWeight: '500'
                    }}
                    autoFocus
                  />
                ) : (
                  <span
                    onClick={(e) => startEditing(tab.id, tab.name, e)}
                    style={{ cursor: 'text' }}
                  >
                    {tab.name}
                  </span>
                )}
                {/* Status dot */}
                {status && status !== 'connected' && (
                  <span style={{
                    width: '7px',
                    height: '7px',
                    borderRadius: '50%',
                    background: status === 'connecting' ? '#f59e0b' :
                               status === 'error' ? '#ef4444' : '#9ca3af',
                    flexShrink: 0
                  }} />
                )}
                {/* Close button */}
                <button
                  onClick={(e) => closeTab(tab.id, e)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '3px',
                    color: '#9ca3af',
                    fontSize: '16px',
                    lineHeight: 1,
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: tabs.length === 1 ? 0.3 : 0.6,
                    transition: 'all 0.15s',
                    width: '20px',
                    height: '20px'
                  }}
                  onMouseEnter={(e) => {
                    if (tabs.length > 1) {
                      e.currentTarget.style.background = '#f3f4f6';
                      e.currentTarget.style.color = '#1e1e1e';
                      e.currentTarget.style.opacity = '1';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = 'transparent';
                    e.currentTarget.style.color = '#9ca3af';
                    e.currentTarget.style.opacity = tabs.length === 1 ? '0.3' : '0.6';
                  }}
                >
                  ×
                </button>
              </div>
            );
          })}
          {/* Add Tab Button */}
          <button
            onClick={addTab}
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '0 12px',
              height: '100%',
              color: '#6b7280',
              fontSize: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'all 0.15s',
              fontWeight: '300'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = '#f3f4f6';
              e.currentTarget.style.color = '#1e1e1e';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
              e.currentTarget.style.color = '#6b7280';
            }}
            title="New Terminal"
          >
            +
          </button>
        </div>
      </div>

      {/* Terminal Sessions */}
      <div style={{ flex: 1, overflow: 'hidden' }}>
        {tabs.map(tab => (
          <TerminalSession
            key={tab.id}
            sessionId={tab.id}
            isActive={isVisible && tab.id === activeTabId}
            onStatusChange={handleStatusChange}
          />
        ))}
      </div>
    </div>
  );
}

export default TerminalView;
